import { z } from 'zod';

export const ITEM_TYPES = ['story', 'bug', 'task', 'epic', 'subtask'] as const;
export const itemTypeEnum = z.enum(ITEM_TYPES);
export type ItemType = z.infer<typeof itemTypeEnum>;

export const ITEM_PRIORITIES = ['lowest', 'low', 'medium', 'high', 'highest', 'critical'] as const;
export const itemPriorityEnum = z.enum(ITEM_PRIORITIES);
export type ItemPriority = z.infer<typeof itemPriorityEnum>;

export const ITEM_STATUSES = ['backlog', 'todo', 'in_progress', 'in_review', 'done'] as const;
export const itemStatusEnum = z.enum(ITEM_STATUSES);
export type ItemStatus = z.infer<typeof itemStatusEnum>;

/**
 * A single issue (story/bug/task/epic/subtask) within a project — Jira's
 * "Item" concept. `status` isn't accepted on create: it's derived
 * server-side from whether a `sprintId` was given (`todo` vs `backlog`),
 * matching the source.
 */
export const createItemSchema = z.object({
  title: z.string().trim().min(2, 'Title must be at least 2 characters').max(300),
  description: z.string().trim().max(5000).optional().or(z.literal('')),
  type: itemTypeEnum.default('task'),
  priority: itemPriorityEnum.default('medium'),
  storyPoints: z.coerce.number().min(0).default(0),
  estimatedHours: z.coerce.number().min(0).default(0),
  assigneeId: z.string().optional(),
  sprintId: z.string().optional(),
  epicId: z.string().optional(),
  parentId: z.string().optional(),
  labels: z.array(z.string().trim().max(40)).default([]),
  dueDate: z.coerce.date().optional(),
});

export type CreateItemInput = z.infer<typeof createItemSchema>;

/** Unlike create, `status` can be set directly here — a normal field edit, not just via `move`. */
export const updateItemSchema = createItemSchema.partial().extend({
  status: itemStatusEnum.optional(),
});

export type UpdateItemInput = z.infer<typeof updateItemSchema>;

/** `sprintId: null` means "move to the backlog" — distinct from omitting it. */
export const moveItemSchema = z.object({
  sprintId: z.string().nullable().optional(),
  status: itemStatusEnum.optional(),
});

export type MoveItemInput = z.infer<typeof moveItemSchema>;

export const itemListQuerySchema = z.object({
  /** The literal `"backlog"` means "no sprint assigned", matching the source's `getItems`. */
  sprintId: z.string().optional(),
  status: itemStatusEnum.optional(),
  assigneeId: z.string().optional(),
  type: itemTypeEnum.optional(),
  search: z.string().trim().max(200).optional(),
});

export type ItemListQuery = z.infer<typeof itemListQuerySchema>;
